<style scoped>
.pool-swatch {
	display: inline-block;
	width: 12px;
	height: 12px;
	border-radius: 3px;
	border: 1px solid rgba(0, 0, 0, 0.2);
	vertical-align: middle;
	margin-right: 4px;
}
.row-clickable {
	cursor: pointer;
	transition: background-color 0.15s ease;
}
.row-clickable:hover {
	background-color: rgba(13, 110, 253, 0.05);
}
</style>

<template>
	<HorizontalSplit>
		<template #top>
			<div style="position: relative; width: 100%; height: 100%">
				<mgl-map
					:map-style="mapStyleURL(ALIDADE_SMOOTH)"
					:center="mapCenter"
					:zoom="tileZoom"
					@map:load="onMapLoad"
				>
					<mgl-geo-json-source source-id="tile-bounds" :data="tileBounds">
						<mgl-line-layer
							layer-id="tile-bounds-outline"
							:paint="{
								'line-color': '#ffc107',
								'line-width': 2.5,
							}"
						/>
					</mgl-geo-json-source>
					<mgl-raster-source
						:key="`planet-${planetSourceTag}`"
						:source-id="`planet-${planetSourceTag}`"
						:tiles="planetTileURLs"
						:tile-size="256"
						:maxzoom="19"
					>
						<mgl-raster-layer
							before="tile-bounds-outline"
							v-if="activeSource === 'planet' && selectedPlanetItemId"
							:layer-id="`planet-${planetSourceTag}`"
						/>
					</mgl-raster-source>
					<mgl-raster-source
						:key="`blue-excess-${planetSourceTag}`"
						:source-id="`blue-excess-${planetSourceTag}`"
						:tiles="blueExcessTileURLs"
						:tile-size="256"
						:maxzoom="19"
					>
						<mgl-raster-layer
							before="tile-bounds-outline"
							v-if="activeSource === 'blue-excess' && selectedPlanetItemId"
							:layer-id="`blue-excess-${planetSourceTag}`"
						/>
					</mgl-raster-source>
					<mgl-raster-source
						:key="`ndwi-${planetSourceTag}`"
						:source-id="`ndwi-${planetSourceTag}`"
						:tiles="ndwiTileURLs"
						:tile-size="256"
						:maxzoom="19"
					>
						<mgl-raster-layer
							before="tile-bounds-outline"
							v-if="activeSource === 'ndwi' && selectedPlanetItemId"
							:layer-id="`ndwi-${planetSourceTag}`"
						/>
					</mgl-raster-source>
					<mgl-raster-source
						:key="`blue-v-red-${planetSourceTag}`"
						:source-id="`blue-v-red-${planetSourceTag}`"
						:tiles="blueVRedTileURLs"
						:tile-size="256"
						:maxzoom="19"
					>
						<mgl-raster-layer
							before="tile-bounds-outline"
							v-if="activeSource === 'blue-v-red' && selectedPlanetItemId"
							:layer-id="`blue-v-red-${planetSourceTag}`"
						/>
					</mgl-raster-source>
					<mgl-raster-source
						:key="`pixel-variation-${planetSourceTag}`"
						:source-id="`pixel-variation-${planetSourceTag}`"
						:tiles="pixelVariationTileURLs"
						:tile-size="256"
						:maxzoom="19"
					>
						<mgl-raster-layer
							before="tile-bounds-outline"
							v-if="activeSource === 'pixel-variation' && selectedPlanetItemId"
							:layer-id="`pixel-variation-${planetSourceTag}`"
						/>
					</mgl-raster-source>
					<mgl-raster-source
						:key="`nir-${planetSourceTag}`"
						:source-id="`nir-${planetSourceTag}`"
						:tiles="nirTileURLs"
						:tile-size="256"
						:maxzoom="19"
					>
						<mgl-raster-layer
							before="tile-bounds-outline"
							v-if="activeSource === 'nir' && selectedPlanetItemId"
							:layer-id="`nir-${planetSourceTag}`"
						/>
					</mgl-raster-source>
					<mgl-raster-source
						source-id="stadia-satellite"
						:tiles="stadiaTileURLs"
						:tile-size="256"
						:maxzoom="19"
					>
						<mgl-raster-layer
							before="tile-bounds-outline"
							v-if="activeSource === 'stadia'"
							layer-id="stadia-satellite"
						/>
					</mgl-raster-source>
					<MglTerradrawSource
						v-if="showTerradraw"
						ref="terradrawRef"
						source-id="drawn-shapes"
						v-model="drawnFeatures"
						@create="onTerradrawCreate"
						@update="onTerradrawUpdate"
					/>
				</mgl-map>

				<SatelliteImageControls
					variant="vertical"
					v-model="activeSource"
					:brightness="brightness"
					@update:brightness="brightness = $event"
				/>
			</div>
		</template>

		<template #bottom>
			<div class="p-3" v-if="store.loading" style="height: 100%">
				<div
					class="d-flex align-items-center justify-content-center h-100 text-muted"
				>
					<div class="text-center">
						<div class="spinner-border text-primary mb-2" role="status"></div>
						<p class="small">
							Loading tile {{ tileZ }}/{{ tileX }}/{{ tileY }}...
						</p>
					</div>
				</div>
			</div>

			<div class="p-3" v-else-if="store.error">
				<div class="alert alert-danger">{{ store.error }}</div>
			</div>

			<div class="p-3" v-else>
				<div class="d-flex justify-content-between align-items-center mb-3">
					<h6 class="mb-0">
						<i class="bi bi-grid-3x3"></i>
						Tile {{ tileZ }}/{{ tileX }}/{{ tileY }}
						<button
							class="btn btn-outline-secondary btn-sm ms-1 py-0"
							title="Keyboard Shortcuts"
							@click="showShortcutsModal = true"
						>
							<i class="bi bi-keyboard"></i>
						</button>
						<button
							class="btn btn-sm ms-1 py-0"
							:class="
								showTerradraw ? 'btn-outline-info' : 'btn-outline-secondary'
							"
							title="Toggle Terradraw layer"
							@click="showTerradraw = !showTerradraw"
						>
							<i :class="showTerradraw ? 'bi-eye-fill' : 'bi-eye-slash'"></i>
						</button>
					</h6>
					<div class="d-flex align-items-center gap-2">
						<span class="text-muted small text-nowrap">New pool:</span>
						<PoolConditionSelect v-model="drawnCondition" />
						<RouterLink
							:to="`/_/sudo/vision-tasks/pool-corpus/item/${itemId}`"
							class="btn btn-outline-secondary btn-sm"
						>
							<i class="bi bi-arrow-left"></i> Back
						</RouterLink>
					</div>
				</div>

				<div v-if="selectedPools.length > 0" class="mb-3">
					<h6>
						<i class="bi bi-hand-index-thumb"></i>
						Selected Pools ({{ selectedPools.length }})
						<button
							class="btn btn-outline-secondary btn-sm py-0 px-1 ms-1"
							title="Deselect all"
							@click="deselectAllPools"
						>
							<i class="bi bi-x-circle"></i>
						</button>
					</h6>
					<div class="table-responsive">
						<table class="table table-sm small mb-0">
							<thead class="table-light">
								<tr>
									<th style="width: 40px"></th>
									<th style="width: 60px">ID</th>
									<th style="width: 130px">Condition</th>
									<th class="text-end" style="width: 100px">Area</th>
									<th style="width: 40px"></th>
									<th style="width: 40px"></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="p in selectedPools" :key="p.id">
									<td>
										<span
											class="pool-swatch"
											:style="{
												backgroundColor:
													CONDITION_COLORS[p.condition] ??
													CONDITION_COLORS['unknown'],
											}"
										></span>
									</td>
									<td>{{ p.id }}</td>
									<td class="text-nowrap" @click.stop>
										<PoolConditionSelect
											:model-value="p.condition"
											@update:model-value="updatePoolCondition(p.id, $event)"
										/>
									</td>
									<td class="text-end">{{ formatArea(p.area) }}</td>
									<td @click.stop>
										<button
											v-if="!isPoolReviewed(p.id)"
											class="btn btn-outline-success btn-sm py-0 px-1"
											title="Approve pool"
											@click="markReviewed(p.id)"
										>
											<i class="bi bi-check-circle"></i>
										</button>
									</td>
									<td @click.stop>
										<button
											class="btn btn-outline-danger btn-sm py-0 px-1"
											title="Delete pool"
											@click="deletePool(p.id)"
										>
											<i class="bi bi-trash"></i>
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
				<p v-else class="text-muted small">
					<i class="bi bi-hand-index"></i>
					Use the select tool to select pools on the map.
				</p>

				<div v-if="createdPoolRows.length > 0" class="mb-3">
					<h6>
						<i class="bi bi-plus-square"></i>
						Created Pools ({{ createdPoolRows.length }})
						<span class="text-muted small fw-normal">
							&mdash; drawn just now, review or delete if wrong
						</span>
					</h6>
					<div class="table-responsive">
						<table class="table table-sm small mb-0">
							<thead class="table-light">
								<tr>
									<th style="width: 40px"></th>
									<th style="width: 60px">ID</th>
									<th style="width: 130px">Condition</th>
									<th class="text-end" style="width: 100px">Area</th>
									<th style="width: 40px"></th>
									<th style="width: 40px"></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="p in createdPoolRows" :key="p.id">
									<td>
										<span
											class="pool-swatch"
											:style="{
												backgroundColor:
													CONDITION_COLORS[p.condition] ??
													CONDITION_COLORS['unknown'],
											}"
										></span>
									</td>
									<td>{{ p.id }}</td>
									<td class="text-nowrap" @click.stop>
										<PoolConditionSelect
											:model-value="p.condition"
											:pool-id="p.id"
											@update:model-value="
												updateCreatedPoolCondition(p.id, $event)
											"
										/>
									</td>
									<td class="text-end">{{ formatArea(p.area) }}</td>
									<td @click.stop>
										<button
											class="btn btn-outline-success btn-sm py-0 px-1"
											title="Mark pool complete"
											@click="markCreatedPoolReviewed(p.id)"
										>
											<i class="bi bi-check-circle"></i>
										</button>
									</td>
									<td @click.stop>
										<button
											class="btn btn-outline-danger btn-sm py-0 px-1"
											title="Delete pool"
											@click="deletePool(p.id)"
										>
											<i class="bi bi-trash"></i>
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>

				<h6>
					Pools
					<button
						class="btn btn-sm py-0 px-1 ms-1"
						:class="showReviewed ? 'btn-outline-secondary' : 'btn-outline-info'"
						:title="
							showReviewed
								? 'Showing all pools'
								: 'Showing unreviewed pools only'
						"
						@click="showReviewed = !showReviewed"
					>
						<i :class="showReviewed ? 'bi-eye-fill' : 'bi-eye'"></i>
					</button>
					<span class="text-muted small ms-1">
						{{ reviewedCount }} of {{ totalCount }} reviewed
					</span>
					<div class="progress mt-1" style="height: 4px; width: 160px">
						<div
							class="progress-bar bg-success"
							:style="{ width: reviewedPct + '%' }"
						></div>
					</div>
				</h6>
				<div class="table-responsive" v-if="store.detail?.pools.length">
					<table class="table table-sm small mb-3">
						<thead class="table-light">
							<tr>
								<th style="width: 40px"></th>
								<th style="width: 60px">ID</th>
								<th style="width: 130px">Condition</th>
								<th>Reviewed</th>
								<th>Creator</th>
								<th>Created</th>
								<th style="width: 40px"></th>
								<th style="width: 40px"></th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="pool in filteredPools"
								:key="pool.id"
								class="row-clickable"
								:class="{ 'table-active': selectedPoolIds.has(pool.id) }"
								@click="fitToPool(pool)"
							>
								<td>
									<span
										class="pool-swatch"
										:style="{
											backgroundColor:
												CONDITION_COLORS[pool.condition] ??
												CONDITION_COLORS['unknown'],
										}"
									></span>
								</td>
								<td>{{ pool.id }}</td>
								<td class="text-nowrap" @click.stop>
									<PoolConditionSelect
										:model-value="pool.condition"
										:pool-id="pool.id"
										@update:model-value="pool.condition = $event"
									/>
								</td>
								<td>
									<span v-if="pool.reviewed" class="badge bg-success">Yes</span>
									<span v-else class="badge bg-danger">No</span>
								</td>
								<td>{{ pool.reviewerId ?? "\u2014" }}</td>
								<td>{{ formatDate(pool.createdAt) }}</td>
								<td @click.stop>
									<button
										v-if="!pool.reviewed"
										class="btn btn-outline-success btn-sm py-0 px-1"
										title="Mark pool complete"
										@click="markReviewed(pool.id)"
									>
										<i class="bi bi-check-circle"></i>
									</button>
								</td>
								<td @click.stop>
									<button
										class="btn btn-outline-danger btn-sm py-0 px-1"
										title="Delete pool"
										@click="deletePool(pool.id)"
									>
										<i class="bi bi-trash"></i>
									</button>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<p v-else class="text-muted small">No pools in this tile.</p>
			</div>
		</template>
	</HorizontalSplit>

	<ToastNotification
		:show="showDeleteError"
		title="Delete Failed"
		:message="deleteErrorMessage"
	/>

	<Teleport to="body">
		<div v-if="showShortcutsModal">
			<div class="modal-backdrop fade show"></div>
			<div
				class="modal fade show d-block"
				tabindex="-1"
				@click.self="showShortcutsModal = false"
			>
				<div class="modal-dialog modal-dialog-centered">
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">
								<i class="bi bi-keyboard"></i>
								Keyboard Shortcuts
							</h5>
							<button
								type="button"
								class="btn-close"
								@click="showShortcutsModal = false"
							></button>
						</div>
						<div class="modal-body">
							<table class="table table-sm small mb-0">
								<tbody>
									<tr>
										<td><kbd>1</kbd>&ndash;<kbd>7</kbd></td>
										<td>
											Raster sources (planet, blue excess, NDWI, blue vs red,
											pixel variation, NIR, stadia)
										</td>
									</tr>
									<tr>
										<td><kbd>s</kbd></td>
										<td>Terradraw move tool (translate pools)</td>
									</tr>
									<tr>
										<td><kbd>e</kbd></td>
										<td>Terradraw edit tool (drag pool vertices)</td>
									</tr>
									<tr>
										<td><kbd>p</kbd></td>
										<td>Terradraw polygon tool</td>
									</tr>
									<tr>
										<td><kbd>r</kbd></td>
										<td>Terradraw rectangle tool</td>
									</tr>
									<tr>
										<td><kbd>c</kbd></td>
										<td>Terradraw circle tool</td>
									</tr>
									<tr>
										<td><kbd>f</kbd></td>
										<td>Terradraw freehand tool</td>
									</tr>
									<tr>
										<td><kbd>d</kbd> / <kbd>Del</kbd></td>
										<td>Delete selected pool</td>
									</tr>
									<tr>
										<td><kbd>b</kbd></td>
										<td>Set condition to blue</td>
									</tr>
									<tr>
										<td><kbd>g</kbd></td>
										<td>Set condition to green</td>
									</tr>
									<tr>
										<td><kbd>t</kbd></td>
										<td>Toggle Terradraw layer visibility</td>
									</tr>
									<tr>
										<td><kbd>u</kbd></td>
										<td>Set condition to unknown</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>
		</div>
	</Teleport>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import {
	MglMap,
	MglRasterSource,
	MglRasterLayer,
	MglGeoJsonSource,
	MglLineLayer,
	useMap,
} from "vue-maplibre-gl";
import { mapStyleURL, boundsFromGeoJSON } from "@/map/util";
import { ALIDADE_SMOOTH } from "@/map/constants";
import MglTerradrawSource from "@/components/MglTerradrawSource.vue";
import HorizontalSplit from "@/components/layout/HorizontalSplit.vue";
import SatelliteImageControls from "@/map/SatelliteImageControls.vue";
import PoolConditionSelect from "@/components/common/PoolConditionSelect.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import { apiClient } from "@/client";
import { usePoolStore } from "@/store/pool";
import { useVisionReviewStore, type ReviewPool } from "@/store/vision_review";

const route = useRoute();
const router = useRouter();
const mapInstance = useMap();
const store = useVisionReviewStore();
const poolStore = usePoolStore();
const terradrawRef = ref<InstanceType<typeof MglTerradrawSource> | null>(null);

const activeSource = ref("planet");
const brightness = ref<number | null>(
	route.query.brightness ? parseFloat(route.query.brightness as string) : null,
);

const brightnessParam = computed(() =>
	brightness.value != null ? `&brightness=${brightness.value}` : "",
);

// Key tag for the planet raster sources. MglRasterSource only adds its source
// once (it does not react to a changing `tiles` prop), so the source must be
// remounted — via `:key`/`:source-id` — whenever the tile URL would change:
// on brightness and on the planet item (now fixed from the route).
const planetSourceTag = computed(
	() =>
		`${brightness.value ?? "default"}-${selectedPlanetItemId.value ?? "any"}`,
);

const planetTileURLs = computed(() => [
	`/api/tile/planet/any/{z}/{x}/{y}?bands=3,2,1${brightnessParam.value}${planetItemParam.value}`,
]);
const blueExcessTileURLs = computed(() => [
	`/api/tile/planet/any/{z}/{x}/{y}?transform=blue-excess${planetItemParam.value}`,
]);
const ndwiTileURLs = computed(() => [
	`/api/tile/planet/any/{z}/{x}/{y}?transform=ndwi${planetItemParam.value}`,
]);
const blueVRedTileURLs = computed(() => [
	`/api/tile/planet/any/{z}/{x}/{y}?transform=blue-v-red${planetItemParam.value}`,
]);
const pixelVariationTileURLs = computed(() => [
	`/api/tile/planet/any/{z}/{x}/{y}?transform=pixel-variation${planetItemParam.value}`,
]);
const nirTileURLs = computed(() => [
	`/api/tile/planet/any/{z}/{x}/{y}?band=4&brightness=auto${planetItemParam.value}`,
]);
const stadiaTileURLs = computed(() => [
	"/api/tile/stadia/alidade_satellite/{z}/{x}/{y}",
]);

const tileZ = parseInt(route.params.z as string, 10);
const tileX = parseInt(route.params.x as string, 10);
const tileY = parseInt(route.params.y as string, 10);
const tileZoom = computed(() => Math.max(tileZ - 2, 0));

const drawnFeatures = ref<GeoJSON.Feature[]>([]);
const initialLoadDone = ref(false);
const syncing = ref(false);
const showDeleteError = ref(false);
const deleteErrorMessage = ref("");
const showShortcutsModal = ref(false);
const showTerradraw = ref(true);
const showReviewed = ref(false);
const drawnCondition = ref("unknown");
// The tile page is scoped to a fixed planet item from the route; the item
// selector was removed, so this is never null.
const itemId = route.params.itemId as string;
const selectedPlanetItemId = computed(() => itemId);

const planetItemParam = computed(() =>
	selectedPlanetItemId.value
		? `&source=${encodeURIComponent(selectedPlanetItemId.value)}`
		: "",
);

const filteredPools = computed(() => {
	if (!store.detail?.pools) return [];
	let pools = store.detail.pools;
	if (!showReviewed.value) pools = pools.filter((p) => !p.reviewed);
	// Pools tied to a planet item only show under that item's source; pools
	// without an item (still need review) always show.
	if (selectedPlanetItemId.value) {
		pools = pools.filter(
			(p) => !p.itemId || p.itemId === selectedPlanetItemId.value,
		);
	}
	return pools;
});

// Pools rendered on the map. Mirrors the list's planet-item rule (pools tied
// to another planet item are excluded; unassigned pools always show), so pool
// geometries belonging to a different selected item never render outside the
// current item's usable area. Unlike `filteredPools`, this is not gated by
// `showReviewed` — reviewed pools stay visible on the map as review context.
const mapPools = computed(() => {
	if (!store.detail?.pools) return [];
	let pools = store.detail.pools;
	if (selectedPlanetItemId.value) {
		pools = pools.filter(
			(p) => !p.itemId || p.itemId === selectedPlanetItemId.value,
		);
	}
	return pools;
});

const totalCount = computed(() => store.detail?.pools.length ?? 0);
const reviewedCount = computed(
	() => store.detail?.pools.filter((p) => p.reviewed).length ?? 0,
);
const reviewedPct = computed(() =>
	totalCount.value
		? Math.round((reviewedCount.value / totalCount.value) * 100)
		: 0,
);

const CONDITION_COLORS: Record<string, string> = {
	unknown: "#facc15",
	blue: "#0b52d6",
	green: "#22c55e",
	dry: "#ef4444",
	partial: "#a855f7",
	covered: "#f97316",
};

watch(
	() => mapPools.value.map((p) => `${p.id}:${p.condition}`),
	() => {
		syncing.value = true;
		const pools = mapPools.value;
		if (!pools.length) {
			drawnFeatures.value = [];
			initialLoadDone.value = false;
			syncing.value = false;
			return;
		}

		const colorByPoolId = new Map<number, string>();
		for (const p of pools) {
			colorByPoolId.set(
				p.id,
				CONDITION_COLORS[p.condition] ?? CONDITION_COLORS["unknown"],
			);
		}

		const targetIds = new Set(pools.map((p) => p.id));
		const drawnPoolIds = new Set(
			drawnFeatures.value
				.map((f) => (f.properties as any)?.poolId as number | undefined)
				.filter((id): id is number => id != null),
		);
		const sameMembership =
			drawnPoolIds.size === targetIds.size &&
			[...targetIds].every((id) => drawnPoolIds.has(id));

		if (sameMembership) {
			// Only conditions changed: sync colors in place so selection and
			// in-progress edits survive a rebuild.
			for (const f of drawnFeatures.value) {
				const poolId = (f.properties as any)?.poolId as number | undefined;
				if (poolId == null) continue;
				const color = colorByPoolId.get(poolId);
				if (color && (f.properties as any).color !== color) {
					(f.properties as any).condition =
						pools.find((p) => p.id === poolId)?.condition ?? "unknown";
					(f.properties as any).color = color;
				}
			}
			drawnFeatures.value = drawnFeatures.value.filter((f) => {
				const poolId = (f.properties as any)?.poolId as number | undefined;
				return poolId == null || targetIds.has(poolId);
			});
			// delay unsetting 'syncing' to ensure that Terradraw settles its events first
			setTimeout(() => {
				syncing.value = false;
			}, 1);
			return;
		}

		// Membership changed (planet item switch, add, or delete): rebuild the
		// map to exactly the current item's pools.
		const features: GeoJSON.Feature[] = [];
		for (const p of pools) {
			if (!p.geometry) continue;
			features.push({
				type: "Feature" as const,
				geometry: p.geometry,
				properties: {
					poolId: p.id,
					condition: p.condition,
					reviewed: p.reviewed,
					color: colorByPoolId.get(p.id) ?? CONDITION_COLORS["unknown"],
					opacity: 0.0,
				},
			});
		}
		features.sort(
			(a, b) =>
				((a.properties as any)?.poolId as number) -
				((b.properties as any)?.poolId as number),
		);
		drawnFeatures.value = features;
		initialLoadDone.value = true;
		syncing.value = false;
	},
);

function tileToLng(tx: number, tz: number): number {
	return (tx / Math.pow(2, tz)) * 360 - 180;
}

function tileToLat(ty: number, tz: number): number {
	const latRad = Math.atan(
		Math.sinh(Math.PI * (1 - (2 * ty) / Math.pow(2, tz))),
	);
	return (latRad * 180) / Math.PI;
}

const tileBoundsCoords = reactive({
	west: 0,
	east: 0,
	north: 0,
	south: 0,
});

const tileBounds = computed<GeoJSON.GeoJSON>(() => {
	const west = tileToLng(tileX, tileZ);
	const east = tileToLng(tileX + 1, tileZ);
	const north = tileToLat(tileY, tileZ);
	const south = tileToLat(tileY + 1, tileZ);

	tileBoundsCoords.west = west;
	tileBoundsCoords.east = east;
	tileBoundsCoords.north = north;
	tileBoundsCoords.south = south;

	return {
		type: "Polygon",
		coordinates: [
			[
				[west, north],
				[east, north],
				[east, south],
				[west, south],
				[west, north],
			],
		],
	};
});

const mapCenter = computed<[number, number]>(() => [
	(tileBoundsCoords.west + tileBoundsCoords.east) / 2,
	(tileBoundsCoords.north + tileBoundsCoords.south) / 2,
]);

function onMapLoad() {
	const map = mapInstance.map;
	if (!map) return;
	const west = tileToLng(tileX, tileZ);
	const east = tileToLng(tileX + 1, tileZ);
	const north = tileToLat(tileY, tileZ);
	const south = tileToLat(tileY + 1, tileZ);
	map.fitBounds([west, south, east, north], {
		padding: 40,
		duration: 0,
	});
}

function formatDate(iso: string): string {
	if (!iso) return "\u2014";
	return new Date(iso).toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

function conditionClass(condition: string): string {
	switch (condition) {
		case "blue":
			return "bg-primary";
		case "dry":
			return "bg-warning text-dark";
		case "green":
			return "bg-success";
		case "murky":
			return "bg-secondary";
		case "covered":
			return "bg-dark";
		case "false pool":
			return "bg-danger";
		default:
			return "bg-light text-dark";
	}
}

interface SelectedPoolRow {
	id: number;
	condition: string;
	area: number;
}

const selectedPools = computed<SelectedPoolRow[]>(() => {
	return drawnFeatures.value
		.filter((f) => {
			if (f.geometry.type === "Point") return false;
			if (!f.properties?.selected) return false;
			const id = (f.properties as any)?.poolId as number | undefined;
			return id != null;
		})
		.map((f) => ({
			id: (f.properties as any).poolId as number,
			condition: ((f.properties as any).condition as string) ?? "unknown",
			area: polygonArea(f),
		}));
});

const selectedPoolIds = computed(
	() => new Set(selectedPools.value.map((p) => p.id)),
);

const createdPoolRows = computed<SelectedPoolRow[]>(() => {
	return store.createdPools.map((p) => ({
		id: p.id,
		condition: p.condition,
		area: geometryArea(p.geometry),
	}));
});

function polygonArea(feature: GeoJSON.Feature): number {
	return geometryArea(feature.geometry);
}

function geometryArea(g: GeoJSON.Geometry): number {
	if (g.type !== "Polygon" && g.type !== "MultiPolygon") return 0;

	const rings: GeoJSON.Position[][] =
		g.type === "Polygon" ? [g.coordinates[0] as GeoJSON.Position[]] : [];

	let latSum = 0;
	let latCount = 0;
	let areaDeg = 0;

	for (const ring of rings) {
		for (let i = 0; i < ring.length; i++) {
			const [lng, lat] = ring[i];
			latSum += lat;
			latCount++;
			if (i > 0) {
				const [prevLng, prevLat] = ring[i - 1];
				areaDeg += prevLng * lat - lng * prevLat;
			}
		}
	}

	if (latCount === 0) return 0;
	const avgLat = (latSum / latCount) * (Math.PI / 180);
	const mPerDegLng = 111320 * Math.cos(avgLat);
	const mPerDegLat = 111320;

	return Math.abs((areaDeg / 2) * mPerDegLng * mPerDegLat);
}

function formatArea(m2: number): string {
	if (m2 >= 10000) return `${(m2 / 10000).toFixed(2)} ha`;
	if (m2 >= 1) return `${m2.toFixed(1)} m²`;
	return `${(m2 * 10000).toFixed(0)} cm²`;
}

function deselectAllPools() {
	const inst = terradrawRef.value?.getTerraDrawInstance();
	if (inst) {
		const snapshot = inst.getSnapshot();
		for (const f of snapshot) {
			if ((f.properties as any)?.selected) {
				inst.deselectFeature(f.id as string | number);
			}
		}
	}
}

function fitToPool(pool: ReviewPool) {
	if (!pool.geometry || !mapInstance.map) return;
	const bounds = boundsFromGeoJSON(pool.geometry, 200);
	if (bounds) {
		mapInstance.map.fitBounds(bounds, { padding: 80, duration: 500 });
	}
}

function isPoolReviewed(poolId: number): boolean {
	return store.detail?.pools.find((p) => p.id === poolId)?.reviewed ?? false;
}

async function markReviewed(poolId: number): Promise<boolean> {
	try {
		await apiClient.JSONPut(
			`/api/vision/train/pool/${encodeURIComponent(poolId)}`,
			{
				reviewedAt: new Date().toISOString(),
				...(selectedPlanetItemId.value
					? { itemId: selectedPlanetItemId.value }
					: {}),
			},
		);
		return true;
	} catch (e: any) {
		deleteErrorMessage.value = e?.message ?? "Failed to mark pool reviewed.";
		showDeleteError.value = true;
		setTimeout(() => {
			showDeleteError.value = false;
		}, 5000);
		return false;
	}
}

// Marking a pool in the "Created Pools" table complete also dismisses it from
// that table: the pool keeps its current status (now reviewed) but is no
// longer pending correction.
async function markCreatedPoolReviewed(poolId: number) {
	if (!(await markReviewed(poolId))) return;
	const idx = store.createdPools.findIndex((p) => p.id === poolId);
	if (idx >= 0) store.createdPools.splice(idx, 1);
}

async function updatePoolCondition(poolId: number, condition: string) {
	if (!store.detail) return;
	const pool = store.detail.pools.find((p) => p.id === poolId);
	if (pool) pool.condition = condition;
	try {
		await apiClient.JSONPut(
			`/api/vision/train/pool/${encodeURIComponent(poolId)}`,
			{
				condition,
				reviewedAt: new Date().toISOString(),
				...(selectedPlanetItemId.value
					? { itemId: selectedPlanetItemId.value }
					: {}),
			},
		);
	} catch (e: any) {
		deleteErrorMessage.value = e?.message ?? "Failed to update pool condition.";
		showDeleteError.value = true;
		setTimeout(() => {
			showDeleteError.value = false;
		}, 5000);
	}
}

async function deletePool(poolId: number) {
	try {
		await poolStore.deletePool(poolId);
	} catch (e: any) {
		deleteErrorMessage.value = e?.message ?? "Failed to delete pool.";
		showDeleteError.value = true;
		setTimeout(() => {
			showDeleteError.value = false;
		}, 5000);
	}
}

// The condition PUT happens inside PoolConditionSelect (via `pool-id`); this
// only mirrors the value locally for immediate feedback before the SSE round
// trip refreshes the stored pool.
function updateCreatedPoolCondition(poolId: number, condition: string) {
	const pool = store.createdPools.find((p) => p.id === poolId);
	if (pool) pool.condition = condition;
}

async function onTerradrawCreate(feature: any) {
	if (syncing.value) return;
	const geom = feature.geometry;
	if (!geom || (geom.type !== "Polygon" && geom.type !== "MultiPolygon"))
		return;
	try {
		await poolStore.addPoolDrawn(
			geom,
			drawnCondition.value,
			selectedPlanetItemId.value ?? undefined,
		);
	} catch (e: any) {
		deleteErrorMessage.value = e?.message ?? "Failed to create pool.";
		showDeleteError.value = true;
		setTimeout(() => {
			showDeleteError.value = false;
		}, 5000);
	}
}

function onTerradrawDelete(feature: any) {
	if (syncing.value) {
		return;
	}
	const poolId = feature?.properties?.poolId as number | undefined;
	if (poolId && poolId > 0) {
		deletePool(poolId);
	}
}

async function onTerradrawUpdate(feature: any) {
	if (syncing.value) return;
	const poolId = feature?.properties?.poolId as number | undefined;
	if (!poolId || poolId < 0) return;
	try {
		await apiClient.JSONPut(
			`/api/vision/train/pool/${encodeURIComponent(poolId)}`,
			{
				geometry: feature.geometry,
				reviewedAt: new Date().toISOString(),
				...(selectedPlanetItemId.value
					? { itemId: selectedPlanetItemId.value }
					: {}),
			},
		);
	} catch (e: any) {
		deleteErrorMessage.value = e?.message ?? "Failed to update pool geometry.";
		showDeleteError.value = true;
		setTimeout(() => {
			showDeleteError.value = false;
		}, 5000);
	}
}

function setConditionOrSelected(condition: string) {
	if (selectedPools.value.length > 0) {
		for (const p of selectedPools.value) {
			updatePoolCondition(p.id, condition);
		}
	} else {
		drawnCondition.value = condition;
	}
}

function onKeyDown(e: KeyboardEvent) {
	const tag = (e.target as HTMLElement).tagName;
	if (
		tag === "INPUT" ||
		tag === "TEXTAREA" ||
		tag === "SELECT" ||
		(e.target as HTMLElement).isContentEditable
	)
		return;

	const sources = [
		"planet",
		"blue-excess",
		"ndwi",
		"blue-v-red",
		"pixel-variation",
		"nir",
		"stadia",
	];
	switch (e.key) {
		case "1":
		case "2":
		case "3":
		case "4":
		case "5":
		case "6":
		case "7":
			activeSource.value = sources[parseInt(e.key) - 1];
			break;
		case "s":
			terradrawRef.value?.switchMode("move");
			break;
		case "e":
			terradrawRef.value?.switchMode("edit");
			break;
		case "p":
			terradrawRef.value?.switchMode("polygon");
			break;
		case "r":
			terradrawRef.value?.switchMode("rectangle");
			break;
		case "c":
			terradrawRef.value?.switchMode("circle");
			break;
		case "f":
			terradrawRef.value?.switchMode("freehand");
			break;
		case "d":
		case "Delete":
			for (const p of selectedPools.value) {
				deletePool(p.id);
			}
			break;
		case "a":
			for (const p of selectedPools.value) {
				markReviewed(p.id);
			}
			break;
		case "b":
			setConditionOrSelected("blue");
			break;
		case "g":
			setConditionOrSelected("green");
			break;
		case "u":
			setConditionOrSelected("unknown");
			break;
		case "t":
			showTerradraw.value = !showTerradraw.value;
			break;
	}
}

onMounted(() => {
	document.addEventListener("keydown", onKeyDown);
});

onUnmounted(() => {
	document.removeEventListener("keydown", onKeyDown);
});

onMounted(() => {
	store.loadItemTileDetail(itemId, tileZ, tileX, tileY);
});

watch(brightness, (v) => {
	router.replace({
		query: {
			...route.query,
			brightness: v == null || v === 1.0 ? undefined : v.toFixed(1),
		},
	});
});
</script>
